import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Trash2 } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomRolesDB } from "@/hooks/useSupabase";
import { PdfDropzone } from "@/components/common/PdfDropzone";
import { InstructionWordLimit, instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  insertCustomRoleInstructionDocument,
  listCustomRoleInstructionDocuments,
} from "@/lib/guideline-attachments";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import { getKnowledgeDocSignedUrl, uploadKnowledgeDoc } from "@/lib/knowledge-docs";
import type { CustomRoleInstructionDocumentRecord } from "@/types";

export interface CustomRoleSheetDraft {
  mode: "create" | "edit" | "customize";
  id?: string;
  name: string;
  role_key: string;
  instructions: string;
}

interface CustomRoleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: CustomRoleSheetDraft | null;
  onSaved?: (roleKey: string) => void;
}

interface PendingDoc {
  key: string;
  file: File;
  extractedText: string;
}

export function CustomRoleSheet({ open, onOpenChange, draft, onSaved }: CustomRoleSheetProps) {
  const { user, getAccessToken } = useAuth();
  const { upsertUserRole } = useCustomRolesDB();

  const [name, setName] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [instructions, setInstructions] = useState("");
  const [savedDocs, setSavedDocs] = useState<CustomRoleInstructionDocumentRecord[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const title = useMemo(() => {
    if (!draft) return "Custom role";
    if (draft.mode === "create") return "New custom role";
    if (draft.mode === "edit") return "Edit custom role";
    return "Customize role";
  }, [draft]);

  useEffect(() => {
    if (!open || !draft) return;
    setName(draft.name);
    setRoleKey(draft.role_key);
    setInstructions(draft.instructions);
    setRemovedIds(new Set());
    setPendingDocs([]);
    setLoadError(null);
    setSaveError(null);
    setCreatedId(null);

    if (draft.mode === "edit" && draft.id) {
      void listCustomRoleInstructionDocuments(draft.id)
        .then(rows => {
          setSavedDocs(rows);
        })
        .catch((e: unknown) => {
          console.error(e);
          setLoadError(e instanceof Error ? e.message : "Failed to load documents");
          setSavedDocs([]);
        });
    } else {
      setSavedDocs([]);
    }
  }, [open, draft]);

  useEffect(() => {
    let cancelled = false;
    async function resolveUrls() {
      const visible = savedDocs.filter(d => !removedIds.has(d.id));
      const next: Record<string, string> = {};
      await Promise.all(
        visible.map(async d => {
          const url = await getKnowledgeDocSignedUrl(d.file_path);
          if (url) next[d.id] = url;
        })
      );
      if (!cancelled) setSignedUrls(next);
    }
    void resolveUrls();
    return () => {
      cancelled = true;
    };
  }, [savedDocs, removedIds]);

  const visibleSaved = savedDocs.filter(d => !removedIds.has(d.id));
  const overLimit = instructionsExceedLimit(instructions);
  const hasReferenceMaterial =
    instructions.trim().length > 0 || visibleSaved.length > 0 || pendingDocs.length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!user || !draft) return;
    if (!hasReferenceMaterial || overLimit) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const existingId = draft.mode === "edit" ? draft.id : createdId ?? undefined;
      const omitIds = [...removedIds];
      if (existingId) {
        for (const p of pendingDocs) {
          const uploaded = await uploadKnowledgeDoc(p.file, "custom-roles");
          await insertCustomRoleInstructionDocument({
            custom_role_id: existingId,
            file_path: uploaded.path,
            file_name: uploaded.fileName,
            extracted_text: p.extractedText,
          });
          setPendingDocs((prev) => prev.filter((doc) => doc.key !== p.key));
        }
      }

      const roleId = await upsertUserRole({
        id: existingId,
        name: name.trim(),
        role_key: roleKey.trim(),
        instructions: instructions.trim(),
        omit_document_ids: omitIds,
      });

      if (!existingId) {
        setCreatedId(roleId);
        for (const p of pendingDocs) {
          const uploaded = await uploadKnowledgeDoc(p.file, "custom-roles");
          await insertCustomRoleInstructionDocument({
            custom_role_id: roleId,
            file_path: uploaded.path,
            file_name: uploaded.fileName,
            extracted_text: p.extractedText,
          });
          setPendingDocs((prev) => prev.filter((doc) => doc.key !== p.key));
        }
      }

      setPendingDocs([]);
      setRemovedIds(new Set());
      onSaved?.(roleKey.trim());
      onOpenChange(false);
    } catch (reason) {
      console.error(reason);
      setSaveError(reason instanceof Error ? reason.message : "Failed to save custom role");
    } finally {
      setIsSaving(false);
    }
  }

  const queuePdf = useCallback((payload: { file: File; extractedText: string }) => {
    setPendingDocs(prev => [...prev, { key: crypto.randomUUID(), ...payload }]);
  }, []);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="p-0">
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className={pageDescriptionMobileHiddenClassName}>
              Role instructions are injected into every review and have a 1,500-word limit. Reference PDFs have no word limit: they are searched during the review and are not pasted into the prompt.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cr-name">
                Role display name
              </Label>
              <Input
                id="cr-name"
                placeholder="In-house counsel"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cr-key">
                Role key
              </Label>
              <Input
                id="cr-key"
                placeholder="in-house-counsel"
                value={roleKey}
                onChange={e => setRoleKey(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cr-instructions">
                Role instructions
              </Label>
              <Textarea
                id="cr-instructions"
                rows={8}
                placeholder="Instructions injected into the review prompt (optional if you add reference PDFs below)"
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
              />
              <InstructionWordLimit
                text={instructions}
                kind="review-role"
                disabled={isSaving}
                onSummarized={setInstructions}
              />
            </div>

            <PdfDropzone
              mode="queue"
              getAccessToken={getAccessToken}
              onQueued={queuePdf}
              fieldLabel="Reference PDFs (optional)"
              disabled={isSaving}
            />

            {loadError ? <p className="text-xs text-[var(--tint-red-fg)] dark:text-red-400">{loadError}</p> : null}
            {saveError ? <p className="text-xs text-[var(--tint-red-fg)] dark:text-red-400">{saveError}</p> : null}

            {visibleSaved.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">Saved reference PDFs</p>
                <ul className="space-y-2">
                  {visibleSaved.map(doc => (
                    <li
                      key={doc.id}
                      className="flex items-center gap-2 rounded-md border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-3 py-2 text-xs dark:border-white/10 dark:bg-slate-900/50"
                    >
                      <span className="min-w-0 flex-1 truncate text-[var(--lm-foreground)] dark:text-slate-300" title={doc.file_name}>
                        {doc.file_name}
                      </span>
                      {signedUrls[doc.id] ? (
                        <a
                          href={signedUrls[doc.id]}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 text-[var(--lm-accent)] hover:text-[#6d28d9] dark:text-violet-300 dark:hover:text-violet-200"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open
                        </a>
                      ) : null}
                      <IconButton
                        type="button"
                        size="sm"
                        variant="destructive"
                        title="Remove"
                        onClick={() => setRemovedIds(prev => new Set(prev).add(doc.id))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {pendingDocs.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">Pending upload (saved with this role)</p>
                <ul className="space-y-2">
                  {pendingDocs.map(p => (
                    <li
                      key={p.key}
                      className="flex items-center gap-2 rounded-md border border-[var(--lm-border-card)] bg-[var(--surface-sunken)] px-3 py-2 text-xs dark:border-violet-400/25 dark:bg-slate-900/50"
                    >
                      <span className="min-w-0 flex-1 truncate text-[var(--lm-foreground)] dark:text-slate-300" title={p.file.name}>
                        {p.file.name}
                      </span>
                      <IconButton
                        type="button"
                        size="sm"
                        variant="destructive"
                        title="Remove"
                        onClick={() => setPendingDocs(prev => prev.filter(x => x.key !== p.key))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!hasReferenceMaterial ? (
              <p className="text-xs text-[var(--tint-amber-fg)] dark:text-amber-200/90">Add instructions text, at least one reference PDF, or both.</p>
            ) : null}
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="surface" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !hasReferenceMaterial || overLimit}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
